import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useLanguage } from "../../context/LanguageContext";
import { Colors } from "../../styles/theme";
import styles from "./PlanBox.styles";

const PlanBox = ({
  planName,
  description,
  price,
  regularPrice,
  showIntroOffer = true,
  planFeatures,
  buttonText,
  handleOnClick,
  planCount = 1,
}) => {
  const { translate, selectedLanguage } = useLanguage();
  const computedWidth = planCount > 0 ? `${100 / planCount}%` : "100%";
  const cardWidth = planCount > 1 ? `calc(${computedWidth} - 16px)` : "100%";
  const displayedRegularPrice = regularPrice || price;
  const displayedPrice = showIntroOffer ? displayedRegularPrice / 2 : displayedRegularPrice;
  const formatPrice = (value) =>
    Number.isInteger(value) ? value.toString() : value.toFixed(2);
  const midpoint = Math.ceil(planFeatures.length / 2);
  const featureColumns = [planFeatures.slice(0, midpoint), planFeatures.slice(midpoint)];
  const translatedButtonText =
    buttonText === "get started" ? translate("create account and start trial") : translate(buttonText);

  return (
    <Box
      elevation={1}
      sx={{
        ...styles.PlanBox_container,
        flex: `1 1 ${cardWidth}`,
        maxWidth: cardWidth,
        minWidth: 240,
      }}
    >
      <Box sx={styles.PlanBox_pricePanel}>
        <Typography sx={styles.PlanBox_badge}>{translate(planName)}</Typography>
        <Box sx={styles.PlanBox_offer}>
          <Typography
            component="p"
            sx={styles.PlanBox_price}
          >
            ${formatPrice(displayedPrice)}
            <Typography
              component="span"
              sx={styles.PlanBox_currency}
            >
              {translate("usd per month")}
            </Typography>
          </Typography>
          {showIntroOffer && (
            <Typography
              component="p"
              sx={styles.PlanBox_introPeriod}
            >
              {translate("pricing first six months")}
            </Typography>
          )}
          <Typography component="p" sx={styles.PlanBox_regularBilling}>
            {showIntroOffer
              ? `${translate("then")} $${formatPrice(displayedRegularPrice)} ${translate("usd per month compact")}. ${translate("cancel anytime")}.`
              : `${translate("pricing regular rate")} $${formatPrice(displayedRegularPrice)} ${translate("usd per month compact")}. ${translate("cancel anytime")}.`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            ...styles.PlanBox_button,
            backgroundColor: buttonText === "subscribed" ? Colors.green : Colors.white,
            color: buttonText === "subscribed" ? Colors.white : Colors.navy,
          }}
          onClick={handleOnClick}
        >
          {translatedButtonText}
          {buttonText !== "subscribed" && <NorthEastIcon sx={styles.PlanBox_buttonIcon} />}
        </Button>
      </Box>
      <Box sx={styles.PlanBox_featuresPanel}>
        <Typography component="h3" sx={styles.PlanBox_featuresTitle}>
          {translate("everything included")}
        </Typography>
        <Box sx={styles.PlanBox_featureColumns}>
          {featureColumns.map((features, columnIndex) => (
            <List key={columnIndex} sx={styles.PlanBox_featureList}>
              {features.map((feature, index) => (
                <ListItem
                  key={`${columnIndex}-${index}`}
                  sx={{
                    ...styles.PlanBox_featureText,
                    direction: selectedLanguage === "ar" ? "rtl" : "ltr",
                  }}
                >
                  <ListItemIcon sx={styles.PlanBox_featureIcon}>
                    <CheckIcon sx={styles.PlanBox_checkCircle} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          ...styles.PlanBox_featureCopy,
                          textAlign: selectedLanguage === "ar" ? "right" : "left",
                        }}
                      >
                        {feature}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PlanBox;
